import { useEffect, useRef } from "react";

import { BaseComponentRegistry } from "../BaseComponentRegistry";
import { useModulesContext } from "../Components/ModulesProvider";

/**
 * Custom hook to access a component registry instance and trigger re-renders when the modules context changes.
 * @param getSingletonInstance Function to get the singleton instance of the registry.
 * @returns The registry instance or null if not available.
 */
function useComponentRegistry<R extends BaseComponentRegistry<T, P>, T extends number, P extends Record<string, any>>(getSingletonInstance: () => R): R {
    const modulesContext = useModulesContext();
    const registry = useRef<R>(getSingletonInstance());

    useEffect(() => {
        const registryModules = registry.current.getModuleNamesHistory();
        const affectedModules = modulesContext.lastAffectedModules;

        for (const moduleName of Array.from(affectedModules)) {
            if (registryModules.has(moduleName)) {
                registry.current = registry.current.shallowCopy(); // create a shallow copy to trigger re-renders
                break;
            }
        }
    // getSingletonInstance should not change, so we can safely ignore it in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modulesContext]);

    return registry.current;
}

export { useComponentRegistry };
